// Offline-aware API wrapper
// Detects offline state, falls back to IndexedDB, caches responses automatically

import { offlineStateService } from './offline-state.service.js';
import { mutationQueue } from '../queue/mutation-queue.js';
import { 
  athletesRepository,
  trainersRepository,
  programsRepository,
  dietsRepository,
  daybooksRepository,
  gymsRepository,
  membershipsRepository,
  settingsRepository,
  paymentsRepository,
  lockersRepository,
  notificationsRepository,
  exercisesRepository,
  mealsRepository,
  createEntityRepository,
} from '../db/repositories.js';
import { 
  generateCacheKey, 
  getCachePolicy, 
  extractEntityType, 
  getCacheTTL,
  isExpired,
} from '../cache/cache-utils.js';
import { CachePolicy } from '../types/index.js';
import { cacheDebugLog } from '../utils/cache-debug.js';
import { fetchWithTimeout } from '../../utils/fetch-with-timeout.js';
import i18n from '../../utils/i18next.js';

class OfflineApiService {
  constructor() {
    this.repositories = {
      athletes: athletesRepository,
      trainers: trainersRepository,
      programs: programsRepository,
      diets: dietsRepository,
      daybooks: daybooksRepository,
      gyms: gymsRepository,
      memberships: membershipsRepository,
      settings: settingsRepository,
      payments: paymentsRepository,
      lockers: lockersRepository,
      notifications: notificationsRepository,
      exercises: exercisesRepository,
      meals: mealsRepository,
    };
  }

  /**
   * Get repository for entity type
   */
  getRepository(entityType) {
    return this.repositories[entityType] || createEntityRepository(entityType);
  }

  /**
   * Execute API request with offline support
   */
  async request(url, options = {}) {
    const { method = 'GET', body, headers = {}, skipCache = false } = options;
    const entityType = extractEntityType(url);
    const cacheKey = generateCacheKey(url, body);
    const repository = this.getRepository(entityType);
    const policy = skipCache ? CachePolicy.NETWORK_ONLY : getCachePolicy(method, url);

    cacheDebugLog('request', {
      method,
      policy,
      entityType,
      cacheKey: cacheKey.slice(0, 120),
      online: offlineStateService.isCurrentlyOnline(),
    });

    // Track pending operation
    offlineStateService.incrementPendingOperations();

    try {
      switch (policy) {
        case CachePolicy.CACHE_FIRST:
          return await this.cacheFirst(url, options, cacheKey, repository);
        
        case CachePolicy.NETWORK_FIRST:
          return await this.networkFirst(url, options, cacheKey, repository);
        
        case CachePolicy.STALE_WHILE_REVALIDATE:
          return await this.staleWhileRevalidate(url, options, cacheKey, repository);
        
        case CachePolicy.NETWORK_ONLY:
          return await this.networkOnly(url, options);
        
        case CachePolicy.CACHE_ONLY:
          return await this.cacheOnly(cacheKey, repository);
        
        default:
          return await this.networkFirst(url, options, cacheKey, repository);
      }
    } finally {
      offlineStateService.decrementPendingOperations();
    }
  }

  /**
   * Cache First strategy
   */
  async cacheFirst(url, options, cacheKey, repository) {
    const cached = await repository.getCachedResponse(cacheKey);
    
    if (cached && !isExpired(cached)) {
      cacheDebugLog('cache-first-hit', { cacheKey: cacheKey.slice(0, 120) });
      console.log('[OfflineAPI] Cache hit:', cacheKey);
      return cached.data;
    }

    // Cache miss or expired, fetch from network
    return await this.fetchAndCache(url, options, cacheKey, repository);
  }

  /**
   * Network First strategy
   */
  async networkFirst(url, options, cacheKey, repository) {
    offlineStateService.syncFromNavigator();

    if (offlineStateService.isCurrentlyOnline()) {
      try {
        const data = await this.fetchAndCache(url, options, cacheKey, repository);
        cacheDebugLog('network-first-network', { cacheKey: cacheKey.slice(0, 120) });
        return data;
      } catch (error) {
        offlineStateService.markNetworkFailure();
        console.log('[OfflineAPI] Network failed, trying cache:', cacheKey);
        cacheDebugLog('network-first-fallback-cache', { cacheKey: cacheKey.slice(0, 120) });
        const cached = await repository.getCachedResponse(cacheKey);
        if (cached) {
          return cached.data;
        }
        throw error;
      }
    } else {
      // Offline, try cache
      const cached = await repository.getCachedResponse(cacheKey);
      if (cached) {
        console.log('[OfflineAPI] Offline cache hit:', cacheKey);
        cacheDebugLog('offline-cache-hit', { cacheKey: cacheKey.slice(0, 120) });
        return cached.data;
      }
      throw new Error(i18n.t('offline.expiredCache'));
    }
  }

  /**
   * Stale While Revalidate strategy
   *
   * When used as a React Query queryFn, returning cached data immediately while
   * "revalidating" in the background breaks refetches: the query settles on stale
   * IndexedDB before the network completes. When online, behave like NETWORK_FIRST
   * (await network, then fall back to cache). When offline, serve non-expired cache.
   */
  async staleWhileRevalidate(url, options, cacheKey, repository) {
    if (offlineStateService.isCurrentlyOnline()) {
      try {
        const data = await this.fetchAndCache(url, options, cacheKey, repository);
        cacheDebugLog('swr-online-network', { cacheKey: cacheKey.slice(0, 120) });
        return data;
      } catch (error) {
        const cached = await repository.getCachedResponse(cacheKey);
        if (cached && !isExpired(cached)) {
          cacheDebugLog('swr-online-fallback-cache', { cacheKey: cacheKey.slice(0, 120) });
          return cached.data;
        }
        throw error;
      }
    }

    const cached = await repository.getCachedResponse(cacheKey);
    if (cached && !isExpired(cached)) {
      cacheDebugLog('swr-offline-cache', { cacheKey: cacheKey.slice(0, 120) });
      return cached.data;
    }

    throw new Error(i18n.t('offline.expiredCache'));
  }

  /**
   * Network Only strategy
   */
  async networkOnly(url, options) {
    if (!offlineStateService.isCurrentlyOnline()) {
      throw new Error('Offline - network only request failed');
    }

    const response = await fetch(url, options);
    
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Cache Only strategy
   */
  async cacheOnly(cacheKey, repository) {
    const cached = await repository.getCachedResponse(cacheKey);
    
    if (cached) {
      return cached.data;
    }

    throw new Error(i18n.t('offline.expiredCache'));
  }

  /**
   * Fetch from network and cache response
   */
  async fetchAndCache(url, options, cacheKey, repository) {
    if (!offlineStateService.isCurrentlyOnline()) {
      throw new Error('Offline - cannot fetch');
    }

    // Add authentication token if available
    const token = localStorage.getItem('accessToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    let response;
    try {
      response = await fetchWithTimeout(url, {
        ...options,
        headers,
        credentials: 'include',
      });
    } catch (error) {
      offlineStateService.markNetworkFailure();
      throw error;
    }
    
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Cache the response
    const entityType = extractEntityType(url);
    const ttl = getCacheTTL(entityType);
    
    await repository.saveResponse(cacheKey, data, { ttl });

    offlineStateService.markNetworkSuccess();
    cacheDebugLog('idb-write', { cacheKey: cacheKey.slice(0, 120), entityType });
    console.log('[OfflineAPI] Cached:', cacheKey);
    return data;
  }

  /**
   * GET request
   */
  async get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post(url, body, options = {}) {
    return this.request(url, { 
      ...options, 
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  /**
   * PUT request
   */
  async put(url, body, options = {}) {
    return this.request(url, { 
      ...options, 
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  /**
   * DELETE request
   */
  async delete(url, options = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  /**
   * Invalidate cache for specific URL
   */
  async invalidateCache(url, body) {
    const cacheKey = generateCacheKey(url, body);
    const entityType = extractEntityType(url);
    const repository = this.getRepository(entityType);
    
    await repository.hardDelete(cacheKey);
    console.log('[OfflineAPI] Cache invalidated:', cacheKey);
  }

  /**
   * Invalidate all cache for entity type
   */
  async invalidateEntityType(entityType) {
    const repository = this.getRepository(entityType);
    await repository.clear();
    console.log('[OfflineAPI] Entity cache cleared:', entityType);
  }

  /**
   * Prefetch data for offline use
   */
  async prefetch(url, options = {}) {
    const cacheKey = generateCacheKey(url, options.body);
    const entityType = extractEntityType(url);
    const repository = this.getRepository(entityType);
    
    try {
      await this.fetchAndCache(url, options, cacheKey, repository);
      return true;
    } catch (error) {
      console.error('[OfflineAPI] Prefetch failed:', error);
      return false;
    }
  }

  /**
   * Batch prefetch multiple URLs
   */
  async batchPrefetch(requests) {
    const results = await Promise.allSettled(
      requests.map(req => this.prefetch(req.url, req.options))
    );

    return {
      successful: results.filter(r => r.status === 'fulfilled').length,
      failed: results.filter(r => r.status === 'rejected').length,
    };
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    const stats = {};
    
    for (const [entityType, repository] of Object.entries(this.repositories)) {
      const all = await repository.getAll();
      stats[entityType] = {
        count: all.length,
        totalSize: JSON.stringify(all).length,
      };
    }

    return stats;
  }

  /**
   * Clear all caches
   */
  async clearAllCaches() {
    await Promise.all(
      Object.values(this.repositories).map(repo => repo.clear())
    );
    console.log('[OfflineAPI] All caches cleared');
  }
}

// Export singleton instance
export const offlineApiService = new OfflineApiService();
